// client/src/components/shopping/BrandProfileModal.tsx (BrandProfileView)
import React, { useState, useMemo, useEffect } from 'react';
import { 
  X, CheckCircle, MapPin, Share2, Users, Package, 
  Instagram, Globe, Ticket, Clock, LayoutGrid, Layers, 
  Bell, Coins, Mail, Info, Store, Flag, AlertTriangle, 
  Link as LinkIcon, Send, ChevronRight, ShieldCheck, 
  Twitter, Facebook, Youtube, Music2, Flame, UserPlus, Loader2, 
  Heart, ChevronLeft, ArrowLeft, MessageSquare, ArrowRight, 
  Lock, Play, Image as ImageIcon, Menu, ShoppingBag, Grid, LayoutTemplate,
  Film, ChevronDown, ArrowUp
} from 'lucide-react';
import { db, auth } from '../../services/firebase'; 
import { 
  doc, updateDoc, arrayUnion, arrayRemove, onSnapshot, getDoc,
  addDoc, serverTimestamp, collection, increment, query, where, getDocs, setDoc 
} from 'firebase/firestore';
import { Product, UserProfile, BrandProfile } from '../../types'; 
import { formatCurrency } from '../../utils/formatters';
import { resolveBrand } from '../../utils/brandResolver';

// ============================================================================
// HELPERS
// ============================================================================
const getContrastYIQ = (hexcolor: string) => {
    if (!hexcolor) return 'white';
    hexcolor = hexcolor.replace("#", "");
    if (hexcolor.length === 3) hexcolor = hexcolor.split('').map(c => c + c).join('');
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? 'black' : 'white';
};

// ============================================================================
// 3D SPINNING LOGO COMPONENT
// ============================================================================
const SpinningLogo = ({ logoUrl, size = 60 }: { logoUrl: string, size?: number }) => {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div className="absolute inset-0 animate-spin-slow">
        <div className="w-full h-full rounded-3xl overflow-hidden border-2 border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.1)]">
          <img src={logoUrl} className="w-full h-full object-cover" alt="3D Logo" />
        </div>
      </div>
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
    </div>
  );
};

// ============================================================================
// PRODUCT CARD
// ============================================================================
const ProductCard = ({ product, onClick, isWishlisted, onToggleWishlist, layout = 'grid', themeStyles }: any) => {
  const cardClass = layout === 'stack' 
    ? `flex items-center gap-4 p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} hover:border-white/30 transition-all cursor-pointer group`
    : 'group cursor-pointer';
  
  const imageClass = layout === 'stack'
    ? 'w-20 h-20 rounded-lg object-cover grayscale group-hover:grayscale-0 transition-all duration-500 shrink-0'
    : `aspect-[3/4] ${themeStyles.card} border ${themeStyles.border} relative overflow-hidden mb-2 rounded-xl`;

  const contentClass = layout === 'stack'
    ? 'flex-1 min-w-0'
    : '';

  return (
    <div onClick={() => onClick(product)} className={cardClass}>
      <div className={imageClass}>
        {product.image || product.images?.[0] ? (
          <img src={product.image || product.images?.[0]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" alt={product.name} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
            <Package size={32} className="text-zinc-600" />
          </div>
        )}
        {layout !== 'stack' && (
          <button onClick={(e) => { e.stopPropagation(); onToggleWishlist(); }} className="absolute top-2 right-2 p-2 bg-black/50 backdrop-blur-md rounded-full hover:bg-white hover:text-black transition-colors z-10">
            <Heart size={16} fill={isWishlisted ? "currentColor" : "none"} />
          </button>
        )}
      </div>
      <div className={contentClass}>
        <h4 className={`font-bold text-sm uppercase truncate ${themeStyles.text}`}>{product.name}</h4>
        {layout === 'stack' ? (
          <div className={`flex items-center justify-between ${themeStyles.subtext}`}>
            <p className="text-xs">{formatCurrency(product.price)}</p>
            {isWishlisted && <Heart size={14} fill="currentColor" className="text-red-500" />}
          </div>
        ) : (
          <p className={`text-xs ${themeStyles.subtext} mt-1`}>{formatCurrency(product.price)}</p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// GIVEAWAY CARD
// ============================================================================
const ProfileGiveawayCard = ({ drop, profile, onOpenProduct, themeStyles }: any) => {
    return (
        <div onClick={onOpenProduct} className={`relative w-full aspect-[2/1] rounded-[2rem] lg:rounded-none overflow-hidden group border ${themeStyles.border} cursor-pointer transition-transform active:scale-95 shadow-2xl`}>
            <img src={drop.image} className="w-full h-full object-cover grayscale opacity-60 group-hover:scale-105 transition-transform duration-700" alt={drop.name} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute top-6 left-6">
                <div className="flex items-center gap-2 mb-2">
                    <Flame size={16} className="text-white fill-white animate-pulse" />
                    <span className="bg-white text-black text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest">Giveaway</span>
                </div>
                <h2 className="text-2xl font-black text-white leading-none">{drop.name}</h2>
            </div>
            <div className="absolute bottom-6 right-6">
                <button className="bg-white text-black px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest hover:scale-105 transition-transform">Enter Now</button>
            </div>
        </div>
    );
};

// ============================================================================
// FRIEND SHARE OVERLAY
// ============================================================================
const FriendShareOverlay = ({ isOpen, onClose, friends, onSend, brandName, loadingFriends }: any) => {
  const [search, setSearch] = useState('');
  const [sentIds, setSentIds] = useState<string[]>([]);

  const filteredFriends = friends.filter((f: any) => 
    (f.displayName || '').toLowerCase().includes(search.toLowerCase()) || 
    (f.username || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-end lg:items-center justify-center p-0 lg:p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full lg:max-w-md bg-zinc-950 border-t lg:border border-white/10 rounded-t-[2.5rem] lg:rounded-[2.5rem] p-8 shadow-2xl animate-in slide-in-from-bottom-10">
        <div className="w-12 h-1.5 bg-white/10 rounded-full mx-auto mb-6 lg:hidden" />
        
        <h3 className="text-white font-black uppercase italic tracking-widest text-xl mb-2 text-center lg:text-left">Send to Friends</h3>
        <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-6 text-center lg:text-left">Sharing: {brandName}</p>
        
        <div className="relative mb-6">
          <input 
            type="text" 
            placeholder="Search friends..." 
            className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-6 text-white text-sm outline-none focus:border-white/30 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="max-h-[350px] overflow-y-auto space-y-2 scrollbar-hide">
          {loadingFriends ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map((friend: any) => (
              <div key={friend.uid} className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-transparent hover:border-white/10 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10 overflow-hidden shrink-0">
                    <img src={friend.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${friend.uid}`} className="w-full h-full object-cover" alt="" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-white text-xs font-black uppercase tracking-tight truncate">{friend.displayName || 'Scoop User'}</p>
                    <p className="text-zinc-500 text-[10px] font-bold truncate">@{friend.username || 'user'}</p>
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    onSend(friend.uid);
                    setSentIds([...sentIds, friend.uid]);
                  }}
                  disabled={sentIds.includes(friend.uid)}
                  className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${
                    sentIds.includes(friend.uid) 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-white text-black hover:scale-105 active:scale-95'
                  }`}
                >
                  {sentIds.includes(friend.uid) ? 'Sent' : 'Send'}
                </button>
              </div>
            ))
          ) : (
            <p className="text-center py-10 text-zinc-600 text-[10px] font-bold uppercase tracking-[0.2em]">No friends found.</p>
          )}
        </div>

        <button onClick={onClose} className="w-full mt-6 py-4 text-zinc-500 hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest border border-white/10 rounded-xl">Done</button>
      </div>
    </div>
  );
};

// ============================================================================
// SHARED RENDERER - Handles ALL 14+ section types (synced with admin)
// ============================================================================
const renderSectionBody = (block: any, products: any[] = []) => {
  const isLight = getContrastYIQ(block.sectionColor || '#000000') === 'black';
  const textCls = isLight ? 'text-black' : 'text-white';
  const subCls = isLight ? 'text-gray-600' : 'text-gray-400';

  switch (block.type) {
    case 'hero_banner':
      return (
        <div className={`relative overflow-hidden ${block.height === 'large' ? 'aspect-[3/4]' : block.height === 'small' ? 'aspect-[16/9]' : 'aspect-[4/5]'}`}>
          {block.mediaType === 'video' && block.videoUrl ? (
            <video src={block.videoUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
          ) : block.imageUrl ? (
            <img src={block.imageUrl} className="absolute inset-0 w-full h-full object-cover" alt="" />
          ) : <div className="absolute inset-0 bg-zinc-800" />}
          <div className="absolute inset-0 bg-black/30" />
          <div className={`absolute inset-0 flex flex-col justify-end p-6 gap-2 ${block.textAlign === 'center' ? 'items-center text-center' : 'items-start text-left'}`}>
            {block.title && <h3 className="text-2xl font-black uppercase text-white leading-tight">{block.title}</h3>}
            {block.subtitle && <p className="text-xs text-white/80">{block.subtitle}</p>}
            {block.ctaText && block.ctaLink && (
              <a href={block.ctaLink} className="mt-2 px-6 py-2.5 bg-white text-black text-[10px] font-black rounded-full inline-block">
                {block.ctaText}
              </a>
            )}
          </div>
        </div>
      );
      
    case 'video':
      return (
        <div className="p-6">
          {(block.title || block.subtitle) && (
            <div className="mb-3">
              {block.title && <h3 className={`text-lg font-black uppercase ${textCls}`}>{block.title}</h3>}
              {block.subtitle && <p className={`text-xs ${subCls}`}>{block.subtitle}</p>}
            </div>
          )}
          <div className="aspect-video bg-black rounded-2xl overflow-hidden">
            {block.videoUrl ? <video src={block.videoUrl} className="w-full h-full object-cover" autoPlay muted loop playsInline /> : <div className="w-full h-full flex items-center justify-center text-white/30"><Film size={28} /></div>}
          </div>
        </div>
      );
      
    case 'countdown': {
      return (
        <div className="p-8 text-center">
          {block.title && <h3 className={`text-xl font-black uppercase ${textCls}`}>{block.title}</h3>}
          {block.subtitle && <p className={`text-xs ${subCls} mt-1`}>{block.subtitle}</p>}
          <div className="flex justify-center gap-2 mt-4">
            {['DD', 'HH', 'MM', 'SS'].map((u) => (
              <div key={u} className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                <span className={`text-sm font-black ${textCls}`}>00</span>
                <span className={`text-[7px] font-bold ${subCls}`}>{u}</span>
              </div>
            ))}
          </div>
          {block.ctaText && block.ctaLink && (
            <a href={block.ctaLink} className={`mt-5 px-6 py-2.5 text-[10px] font-black rounded-full inline-block ${isLight ? 'bg-black text-white' : 'bg-white text-black'}`}>
              {block.ctaText}
            </a>
          )}
        </div>
      );
    }
    
    case 'rich_text':
      return (
        <div className="p-6 text-center">
          {block.title && <h3 className={`text-xl font-black ${textCls}`}>{block.title}</h3>}
          {block.content && <p className={`text-sm mt-2 leading-relaxed ${subCls}`}>{block.content}</p>}
          {block.ctaText && block.ctaLink && (
            <a href={block.ctaLink} className={`mt-4 px-6 py-2.5 text-[10px] font-black rounded-full inline-block ${isLight ? 'bg-black text-white' : 'bg-white text-black'}`}>
              {block.ctaText}
            </a>
          )}
        </div>
      );
      
    case 'image_with_text':
      return (
        <div className={`p-6 flex gap-4 items-center ${block.imagePosition === 'right' ? 'flex-row-reverse' : ''}`}>
          <div className="w-1/2 aspect-square bg-zinc-800 rounded-2xl overflow-hidden shrink-0">
            {block.imageUrl && <img src={block.imageUrl} className="w-full h-full object-cover" alt="" />}
          </div>
          <div className="w-1/2">
            {block.title && <h3 className={`text-base font-black ${textCls}`}>{block.title}</h3>}
            {block.content && <p className={`text-[11px] mt-1.5 ${subCls} line-clamp-4`}>{block.content}</p>}
            {block.ctaText && block.ctaLink && (
              <a href={block.ctaLink} className={`mt-3 px-4 py-2 text-[9px] font-black rounded-full inline-block ${isLight ? 'bg-black text-white' : 'bg-white text-black'}`}>
                {block.ctaText}
              </a>
            )}
          </div>
        </div>
      );
      
    case 'faq':
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 ${textCls}`}>{block.title}</h3>}
          <div className="space-y-2">
            {(block.items || []).length === 0 && <p className={`text-[10px] ${subCls}`}>No questions added yet.</p>}
            {(block.items || []).slice(0, 4).map((it: any, i: number) => (
              <div key={i} className={`p-3 rounded-xl ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                <div className={`text-xs font-black flex items-center justify-between ${textCls}`}>
                  {it.question || 'Question'} <ChevronDown size={13} />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
      
    case 'featured_collection':
    case 'product_showcase': {
      let displayProducts = [];
      if (block.type === 'featured_collection') {
        displayProducts = products.filter(p => block.collectionId ? p.collectionId === block.collectionId || p.collection === block.collectionId : true).slice(0, block.limit || 8);
      } else {
        displayProducts = block.productIds && block.productIds.length > 0
          ? products.filter(p => block.productIds.includes(p.id))
          : products.slice(0, 4);
      }
      const isGrid = block.productLayout === 'grid';
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 ${textCls}`}>{block.title}</h3>}
          <div className={`grid ${isGrid ? 'grid-cols-2 gap-2' : 'grid-cols-1 gap-2'}`}>
            {displayProducts.length > 0 ? displayProducts.slice(0, 4).map((p: any, i: number) => (
              <div key={p?.id || i} className={`relative aspect-square rounded-xl overflow-hidden ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                {p?.image && <img src={p.image} className="w-full h-full object-cover" alt={p.name} />}
                {block.type === 'product_showcase' && (
                  <div className="absolute bottom-2 left-2 right-2 bg-black/80 rounded-full p-1 px-3 flex items-center justify-between">
                    <span className="text-white text-[8px] font-black truncate">{p?.name || 'Product'}</span>
                    <span className="text-white/60 text-[8px] font-black">{p?.price ? `$${p.price}` : ''}</span>
                  </div>
                )}
              </div>
            )) : (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`aspect-square rounded-xl flex items-center justify-center ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                  <span className={`text-[10px] ${subCls}`}>{block.type === 'product_showcase' ? 'No products selected' : 'No products'}</span>
                </div>
              ))
            )}
          </div>
        </div>
      );
    }
    
    case 'collection_list':
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 ${textCls}`}>{block.title}</h3>}
          <div className="grid grid-cols-2 gap-2">
            {(block.collectionIds?.length ? block.collectionIds : ['', '']).slice(0, 4).map((c: any, i: number) => (
              <div key={i} className={`aspect-[4/3] rounded-xl flex items-end p-2 ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                <span className={`text-[9px] font-black ${textCls}`}>Collection</span>
              </div>
            ))}
          </div>
        </div>
      );
      
    case 'category_tiles': {
      const items = block.items || [];
      const columns = parseInt(block.columns || '3');
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 ${textCls}`}>{block.title}</h3>}
          <div className={`grid gap-2 ${columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
            {(items.length ? items : [{}, {}, {}]).map((tile: any, i: number) => (
              <div key={i} className={`relative aspect-[4/5] rounded-xl overflow-hidden ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                {tile.imageUrl && <img src={tile.imageUrl} className="w-full h-full object-cover" alt="" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className={`text-white text-[11px] font-black uppercase`}>{tile.label || 'Category'}</span>
                  <div className="w-7 h-7 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                    <ArrowUp className="rotate-45" size={12} color="white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    case 'testimonials':
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 text-center ${textCls}`}>{block.title}</h3>}
          <div className="space-y-2">
            {(block.items || []).length === 0 && <p className={`text-[10px] text-center ${subCls}`}>No quotes added yet.</p>}
            {(block.items || []).slice(0, 2).map((it: any, i: number) => (
              <div key={i} className={`p-3 rounded-xl text-center ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                <p className={`text-[11px] italic ${textCls}`}>"{it.quote || 'Great products.'}"</p>
                <p className={`text-[9px] mt-1 font-black ${subCls}`}>— {it.author || 'Customer'}</p>
              </div>
            ))}
          </div>
        </div>
      );
      
    case 'logo_list':
      return (
        <div className="p-6 text-center">
          {block.title && <h3 className={`text-[10px] font-black uppercase tracking-widest mb-3 ${subCls}`}>{block.title}</h3>}
          <div className="flex justify-center gap-3 flex-wrap">
            {(block.images?.length ? block.images : ['', '', '']).slice(0, 4).map((img: string, i: number) => (
              <div key={i} className={`w-12 h-8 rounded-md flex items-center justify-center ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                {img && <img src={img} className="max-w-full max-h-full object-contain" alt="" />}
              </div>
            ))}
          </div>
        </div>
      );
      
    case 'instagram_feed': {
      const images = block.images || [];
      const rows = parseInt(block.rows || '1');
      const totalImages = rows * 4;
      const displayImages = images.slice(0, totalImages);
      return (
        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              {block.title && <h3 className={`text-lg font-black uppercase ${textCls}`}>{block.title}</h3>}
              {block.handle && (
                <a href={`https://instagram.com/${block.handle.replace('@', '')}`} target="_blank" rel="noreferrer" className={`text-sm font-bold text-pink-500 ${textCls}`}>
                  {block.handle}
                </a>
              )}
            </div>
            <a href={`https://instagram.com/${block.handle?.replace('@', '') || ''}`} target="_blank" rel="noreferrer" className="px-4 py-2 bg-pink-500 text-white rounded-full text-[10px] font-black uppercase">
              <Instagram size={14} className="inline mr-1" /> Follow
            </a>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {displayImages.length > 0 ? displayImages.map((img: string, i: number) => (
              <div key={i} className={`aspect-square rounded-lg overflow-hidden ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                {img && <img src={img} className="w-full h-full object-cover" alt="" />}
              </div>
            )) : (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`aspect-square rounded-lg ${isLight ? 'bg-black/5' : 'bg-white/10'}`} />
              ))
            )}
          </div>
        </div>
      );
    }
    
    case 'newsletter':
      return (
        <div className="p-8 text-center">
          {block.title && <h3 className={`text-lg font-black uppercase ${textCls}`}>{block.title}</h3>}
          {block.subtitle && <p className={`text-xs mt-1 ${subCls}`}>{block.subtitle}</p>}
          <div className={`mt-4 flex rounded-full overflow-hidden border ${isLight ? 'border-black/20' : 'border-white/20'}`}>
            <div className={`flex-1 px-4 py-2.5 text-[10px] text-left ${subCls}`}>you@email.com</div>
            <button className={`px-4 py-2.5 text-[9px] font-black ${isLight ? 'bg-black text-white' : 'bg-white text-black'}`}>{block.ctaText || 'Subscribe'}</button>
          </div>
        </div>
      );
      
    case 'gallery':
      return (
        <div className="p-6">
          {block.title && <h3 className={`text-lg font-black uppercase mb-3 ${textCls}`}>{block.title}</h3>}
          <div className="grid grid-cols-3 gap-1.5">
            {(block.images?.length ? block.images : ['', '', '']).slice(0, 6).map((img: string, i: number) => (
              <div key={i} className={`aspect-square rounded-lg overflow-hidden ${isLight ? 'bg-black/5' : 'bg-white/10'}`}>
                {img && <img src={img} className="w-full h-full object-cover" alt="" />}
              </div>
            ))}
          </div>
        </div>
      );
      
    case 'spacer':
      return <div className={block.size === 'large' ? 'h-16' : block.size === 'small' ? 'h-4' : 'h-8'} />;
      
    default:
      return null;
  }
};

// ============================================================================
// MAIN BRAND PROFILE VIEW
// ============================================================================
export const BrandProfileView: React.FC<BrandProfileViewProps> = ({
  brand: initialBrand, products = [], onClose, onSelectProduct,
  userProfile, onUpdateProfile, wishlist = [], onToggleWishlist = () => {}, onShareProfile, requireAuth = (action) => action()
}) => {
  const [liveBrand, setLiveBrand] = useState<any>(initialBrand);
  const [activeTab, setActiveTab] = useState<'shop' | 'collections' | 'about'>('shop');
  const [selectedCollection, setSelectedCollection] = useState<any | null>(null);
  const [brandCollections, setBrandCollections] = useState<any[]>([]); 
  const [isFollowing, setIsFollowing] = useState(false);
  
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showFriendOverlay, setShowFriendOverlay] = useState(false);
  const [userFriends, setUserFriends] = useState<any[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportStatus, setReportStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportStatus, setSupportStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  // GATEKEEPER STATE
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  
  const currentUserId = userProfile?.uid || (userProfile as any)?.id || auth?.currentUser?.uid;

  // ============================================================================
  // DATA DEFINITIONS
  // ============================================================================
  const storefront = liveBrand?.storefront || {};
  const effectiveStatus = isUnlocked ? 'open' : (storefront.storeStatus || 'open');
  const isCustomMode = storefront.enabled === true && (storefront.layout?.length > 0);

  const bName = liveBrand.businessName || liveBrand.name || 'Brand Name';
  const bLogo = liveBrand.logoUrl || liveBrand.logo;
  const bBanner = liveBrand.bannerUrl || liveBrand.banner;
  const bDesc = liveBrand.description || liveBrand.bio || 'Official Brand Profile';
  const bLoc = liveBrand.location ? `${liveBrand.location}, ${liveBrand.province || ''}` : 'Global';
  const bColor = liveBrand.primaryColor || '#ffffff';
  const isDarkColor = getContrastYIQ(bColor) === 'white';
  
  const bFontClass = liveBrand.fontFamily === 'serif' ? 'font-serif' : liveBrand.fontFamily === 'mono' ? 'font-mono' : 'font-sans';
  const bAlignClass = liveBrand.logoAlignment === 'center' ? 'items-center text-center' : liveBrand.logoAlignment === 'right' ? 'items-end text-right' : 'items-start text-left';

  const shopProducts = products.filter((p: any) => p.businessId === liveBrand.id && p.type !== 'raffle');
  const giveaways = products.filter((p: any) => p.businessId === liveBrand.id && p.type === 'raffle');
  const activeCollectionProducts = selectedCollection 
    ? shopProducts.filter((p: any) => p.collectionId === selectedCollection.id || p.collection === selectedCollection.title)
    : [];

  const displayFollowers = liveBrand.followersCount || liveBrand.followers || 0;
  const isWishlisted = (id: string) => wishlist?.some(w => (typeof w === 'string' ? w === id : w.productId === id));

  const layoutBlocks = storefront.layout || [];
  const footerSocialLinks = liveBrand.footerSocialLinks || {};
  const footerText = liveBrand.footerText || '';

  const navSettings = storefront.navigation || {};
  const showStoreName = navSettings.showStoreName !== undefined ? navSettings.showStoreName : true;
  const showFollow = navSettings.showFollow !== undefined ? navSettings.showFollow : true;
  const showShare = navSettings.showShare !== undefined ? navSettings.showShare : true;
  const showReport = navSettings.showReport !== undefined ? navSettings.showReport : true;
  const use3DLogo = liveBrand.use3DLogo || false;

  // ============================================================================
  // THEME STYLES - FORCE DARK FOR DEFAULT MODE
  // ============================================================================
  const themeStyles = useMemo(() => {
    // ✅ FORCE DARK THEME FOR DEFAULT MODE
    if (!isCustomMode) {
      return {
        bg: 'bg-black',
        text: 'text-white',
        subtext: 'text-zinc-400',
        border: 'border-white/10',
        card: 'bg-zinc-900',
        cardHover: 'hover:bg-zinc-800',
        invert: 'bg-white text-black',
      };
    }
    
    // Custom mode uses preset
    const preset = storefront.themePreset || 'standard';
    
    const styles = {
      bg: 'bg-black',
      text: 'text-white',
      subtext: 'text-zinc-400',
      border: 'border-white/10',
      card: 'bg-zinc-900',
      cardHover: 'hover:bg-zinc-800',
      invert: 'bg-white text-black',
    };

    switch(preset) {
      case 'minimalist':
        return {
          ...styles,
          bg: 'bg-white',
          text: 'text-black',
          subtext: 'text-zinc-500',
          border: 'border-zinc-200',
          card: 'bg-zinc-50',
          cardHover: 'hover:bg-zinc-100',
          invert: 'bg-black text-white',
        };
      case 'industrial':
        return {
          ...styles,
          bg: 'bg-zinc-950',
          text: 'text-zinc-100',
          subtext: 'text-zinc-500',
          border: 'border-zinc-800',
          card: 'bg-zinc-900/50',
          cardHover: 'hover:bg-zinc-800/50',
          invert: 'bg-white text-black',
        };
      case 'cinematic':
        return {
          ...styles,
          bg: 'bg-black',
          text: 'text-white',
          subtext: 'text-zinc-300',
          border: 'border-white/20',
          card: 'bg-white/5 backdrop-blur-md',
          cardHover: 'hover:bg-white/10',
          invert: 'bg-white text-black',
        };
      default:
        return styles;
    }
  }, [storefront.themePreset, isCustomMode]);

  // ============================================================================
  // BACKGROUND STYLES (Only for Custom Mode)
  // ============================================================================
  const backgroundStyles = useMemo(() => {
    if (!isCustomMode) return {};
    
    const bgType = liveBrand?.backgroundType || 'color';
    const bgColor = liveBrand?.backgroundColor || '#000000';
    const bgImage = liveBrand?.backgroundImage || '';
    const bgGradient = liveBrand?.backgroundGradient || 'linear-gradient(135deg, #000000, #1a1a1a)';

    switch(bgType) {
      case 'color':
        return { background: bgColor };
      case 'image':
        return { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' };
      case 'gradient':
        return { background: bgGradient };
      default:
        return { background: '#000000' };
    }
  }, [liveBrand, isCustomMode]);

  // ============================================================================
  // FETCH DATA
  // ============================================================================
  useEffect(() => {
      if (userProfile && liveBrand) {
          setIsFollowing(!!userProfile.followingBrands?.includes(liveBrand.id));
      }
  }, [userProfile?.followingBrands, liveBrand?.id]);

  useEffect(() => {
    if (!initialBrand?.id) return;
    const unsub = onSnapshot(doc(db, "users", initialBrand.id), (d) => {
      if (d.exists()) setLiveBrand((p: any) => ({ ...p, ...d.data() }));
    });
    return () => unsub();
  }, [initialBrand?.id]);

  useEffect(() => {
    if (!liveBrand?.id) return;
    const q = query(collection(db, "collections"), where("businessId", "==", liveBrand.id));
    getDocs(q)
      .then(s => setBrandCollections(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(err => console.error("Could not load collections:", err.message)); 
  }, [liveBrand?.id]);

  useEffect(() => {
    if (!userProfile?.uid || !showFriendOverlay) return;
    
    const fetchFriends = async () => {
      setLoadingFriends(true);
      if (userProfile.followingUsers && userProfile.followingUsers.length > 0) {
        try {
          const friendPromises = userProfile.followingUsers.map(uid => getDoc(doc(db, 'users', uid)));
          const friendDocs = await Promise.all(friendPromises);
          const friendsData = friendDocs
            .filter(d => d.exists())
            .map(d => ({ uid: d.id, ...d.data() }));
          setUserFriends(friendsData);
        } catch(e) { 
          console.error("Error fetching friends:", e); 
        }
      } else {
        setUserFriends([]);
      }
      setLoadingFriends(false);
    };
    fetchFriends();
  }, [userProfile, showFriendOverlay]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handlePasswordSubmit = () => {
    if (passwordInput === storefront.gatePassword) {
      setIsUnlocked(true);
    } else {
      alert("Incorrect master password.");
    }
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail) return;
    setWaitlistJoined(true);
    try {
        await addDoc(collection(db, 'waitlist'), {
            brandId: liveBrand.id,
            email: waitlistEmail,
            userId: currentUserId || 'guest',
            createdAt: serverTimestamp()
        });
    } catch(e) {
        console.error("Failed to join waitlist:", e);
    }
  };

  const handleFollowToggle = () => {
    requireAuth(async () => {
      if (!currentUserId) return;
      const willFollow = !isFollowing;
      setIsFollowing(willFollow); 
      try {
        await setDoc(doc(db, 'users', currentUserId), { followingBrands: willFollow ? arrayUnion(liveBrand.id) : arrayRemove(liveBrand.id) }, { merge: true });
        if (onUpdateProfile) onUpdateProfile({}); 
        try {
            await setDoc(doc(db, 'users', liveBrand.id), { followers: increment(willFollow ? 1 : -1) }, { merge: true });
        } catch (brandErr) {}
      } catch(e) { 
        setIsFollowing(!willFollow); 
      }
    }, `Log in to follow @${bName} and see their latest drops.`);
  };

  const handleSubmitReport = () => {
    requireAuth(async () => {
      if (!reportReason) return;
      setReportStatus('submitting');
      try {
          await addDoc(collection(db, 'reports'), {
              targetId: liveBrand.id, targetName: bName, targetType: 'brand',
              reporterId: currentUserId || 'anonymous', reason: reportReason,
              createdAt: serverTimestamp(), status: 'pending'
          });
          setReportStatus('success');
          setTimeout(() => { setShowReportModal(false); setReportStatus('idle'); setReportReason(''); }, 2000);
      } catch (error) { setReportStatus('idle'); }
    }, "Log in to report a brand.");
  };

  const handleSendSupportMessage = () => {
    requireAuth(async () => {
      if (!supportMessage.trim()) return;
      setSupportStatus('submitting');
      try {
          await addDoc(collection(db, 'supportTickets'), {
              brandId: liveBrand.id, brandName: bName, userId: currentUserId || 'anonymous',
              userEmail: userProfile?.email || 'N/A', message: supportMessage,
              createdAt: serverTimestamp(), status: 'open'
          });
          setSupportStatus('success');
          setTimeout(() => { setShowSupportModal(false); setSupportStatus('idle'); setSupportMessage(''); }, 2000);
      } catch (error) { setSupportStatus('idle'); }
    }, `Log in to contact @${bName}.`);
  };

  const shareUrl = `${window.location.origin}/?brand=${liveBrand.slug || liveBrand.id}`;
  const shareText = `Check out ${bName} on Scoop Hype! 🔥`;

  const handleCopyLink = () => { navigator.clipboard.writeText(shareUrl); setShowShareMenu(false); alert("Store link copied to clipboard!"); };
  const handleNativeShare = async () => {
    if (userProfile) { setShowShareMenu(false); setShowFriendOverlay(true); } 
    else {
      if (navigator.share) {
        try { await navigator.share({ title: bName, text: shareText, url: shareUrl }); setShowShareMenu(false); } 
        catch (err) { console.error("Share failed:", err); }
      } else { handleCopyLink(); }
    }
  };

  const handleSendToFriend = async (friendUid: string) => {
    if (!currentUserId) return;
    try {
      await addDoc(collection(db, "messages"), {
          senderId: currentUserId, recipientId: friendUid, text: `Check out ${bName} on Scoop!`,
          shareType: 'brand', brandId: liveBrand.id, createdAt: serverTimestamp(), read: false
      });
    } catch (error) {}
  };

  // ============================================================================
  // COLLECTION DETAILS VIEW
  // ============================================================================
  const renderCollectionDetails = () => (
      <div className="animate-in slide-in-from-right-8 duration-300 w-full">
          <button onClick={() => setSelectedCollection(null)} className={`flex items-center gap-2 ${themeStyles.subtext} hover:${themeStyles.text} text-[10px] font-black uppercase tracking-widest mb-8 transition-colors`}>
              <ArrowLeft size={16} /> Back to Collections
          </button>
          
          <div className="mb-10 flex items-end gap-4">
             <div className={`w-16 h-16 rounded-xl overflow-hidden shrink-0 border ${themeStyles.border}`}>
                 <img src={selectedCollection.imageUrl} className="w-full h-full object-cover grayscale" alt="" />
             </div>
             <div>
                <h2 className={`text-3xl font-black italic uppercase tracking-widest leading-none ${themeStyles.text}`}>
                    {selectedCollection.title}
                </h2>
                <p className={`text-xs ${themeStyles.subtext} font-bold uppercase tracking-widest mt-2`}>
                    {activeCollectionProducts.length} {activeCollectionProducts.length === 1 ? 'Item' : 'Items'}
                </p>
             </div>
          </div>

          {activeCollectionProducts.length === 0 ? (
              <div className={`text-center py-20 border ${themeStyles.border} border-dashed rounded-3xl ${themeStyles.card} backdrop-blur-sm`}>
                  <Package className={`mx-auto ${themeStyles.subtext} mb-4`} size={32} />
                  <h3 className={`${themeStyles.text} font-black uppercase tracking-widest text-sm mb-2`}>Dropping Soon</h3>
                  <p className={`${themeStyles.subtext} text-xs font-bold uppercase tracking-widest`}>No pieces in this collection yet.</p>
              </div>
          ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-6 gap-y-12">
                  {activeCollectionProducts.map((p: any) => (
                      <ProductCard key={p.id} product={p} onClick={() => { onClose(); onSelectProduct(p); }} isWishlisted={isWishlisted(p.id)} onToggleWishlist={() => onToggleWishlist && onToggleWishlist(p.id)} themeStyles={themeStyles} />
                  ))}
              </div>
          )}
      </div>
  );

  // ============================================================================
  // GATEKEEPER VIEW
  // ============================================================================
  if (effectiveStatus !== 'open') {
      return (
          <div className={`fixed inset-0 z-[100] flex flex-col items-center justify-center ${themeStyles.bg} ${themeStyles.text}`}>
              {bBanner && <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: `url(${bBanner})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(10px)' }} />}
              
              <div className="relative z-10 w-full max-w-md p-6 flex flex-col items-center text-center animate-in zoom-in-95 duration-500">
                  <button onClick={onClose} className={`absolute -top-12 right-0 p-2 ${themeStyles.subtext} hover:${themeStyles.text}`}><X size={24} /></button>
                  
                  <div className="w-24 h-24 bg-white rounded-3xl mb-8 overflow-hidden shadow-2xl flex items-center justify-center border-4 border-white/10">
                      {bLogo ? <img src={bLogo} className="w-full h-full object-cover" /> : <span className="text-[10px] font-black text-zinc-300">LOGO</span>}
                  </div>
                  
                  <h2 className={`text-4xl font-black uppercase tracking-tighter text-white leading-none mb-4 ${themeStyles.text}`}>
                      {storefront.gateHeader || "COMING SOON"}
                  </h2>
                  <p className={`text-sm ${themeStyles.subtext} font-medium mb-10 max-w-sm`}>
                      {storefront.gateSubtext || "Join the waitlist for exclusive access."}
                  </p>

                  {effectiveStatus === 'waitlist' ? (
                      waitlistJoined ? (
                          <div className="flex flex-col items-center text-green-400 animate-in fade-in slide-in-from-bottom-4">
                              <CheckCircle size={32} className="mb-2" />
                              <span className="font-black uppercase tracking-widest text-xs">You're on the list</span>
                          </div>
                      ) : (
                          <div className="w-full flex border border-white/20 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm focus-within:border-white/50 transition-colors">
                              <input 
                                  value={waitlistEmail}
                                  onChange={(e) => setWaitlistEmail(e.target.value)}
                                  className="bg-transparent p-4 flex-1 text-sm text-white placeholder-zinc-500 outline-none" 
                                  placeholder="Enter Email Address" 
                                  type="email"
                              />
                              <button onClick={handleWaitlistSubmit} className="px-6 bg-white text-black flex items-center justify-center font-black uppercase text-[10px] tracking-widest hover:bg-zinc-200 transition-colors">
                                  <ArrowRight size={20}/>
                              </button>
                          </div>
                      )
                  ) : (
                      <div className="w-full space-y-4">
                          <div className="w-full flex border border-white/20 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm focus-within:border-white/50 transition-colors">
                              <input 
                                  type="password" 
                                  value={passwordInput}
                                  onChange={(e) => setPasswordInput(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                                  className="bg-transparent p-4 flex-1 text-sm text-white placeholder-zinc-500 outline-none text-center tracking-[0.2em]" 
                                  placeholder="ENTER PASSWORD" 
                              />
                              <button onClick={handlePasswordSubmit} className="px-6 bg-white text-black flex items-center justify-center hover:bg-zinc-200 transition-colors">
                                  <Lock size={16}/>
                              </button>
                          </div>
                      </div>
                  )}

                  <div className="flex gap-4 mt-12 opacity-50">
                      {liveBrand.instagramHandle && <a href={`https://instagram.com/${liveBrand.instagramHandle}`} target="_blank" rel="noreferrer"><Instagram size={20}/></a>}
                      {liveBrand.tiktokHandle && <a href={`https://tiktok.com/@${liveBrand.tiktokHandle}`} target="_blank" rel="noreferrer"><Music2 size={20}/></a>}
                  </div>
              </div>
          </div>
      );
  }

  // ============================================================================
  // MAIN RENDER - CUSTOM MODE with UPDATED RENDERER
  // ============================================================================
  if (isCustomMode) {
    return (
      <div className={`fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-300`} style={backgroundStyles}>
        {/* Custom Navigation - Floating centered bar */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 w-[92%] max-w-lg">
          <nav className="relative w-full bg-black/80 backdrop-blur-xl border border-white/10 rounded-full px-4 py-2 flex items-center justify-between shadow-2xl">
            
            {/* Left Side: Back + Follow */}
            <div className="flex items-center gap-2">
              <button 
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center border border-white/30 hover:bg-white/10 rounded-full transition-all text-white"
              >
                <ChevronLeft size={16} strokeWidth={2.5} />
              </button>
              
              {showFollow && (
                <button 
                  onClick={handleFollowToggle}
                  className={`flex items-center gap-1.5 bg-white text-black hover:bg-gray-200 px-3.5 py-1.5 rounded-full transition-all text-[11px] font-medium ${
                    isFollowing ? 'opacity-50' : ''
                  }`}
                >
                  <UserPlus size={13} />
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
            </div>

            {/* Center: Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2 -top-3">
              <div className="w-14 h-14 rounded-2xl border-2 border-white/30 flex items-center justify-center shadow-2xl overflow-hidden bg-black">
                {bLogo && use3DLogo ? (
                  <SpinningLogo logoUrl={bLogo} size={56} />
                ) : bLogo ? (
                  <img src={bLogo} className="w-full h-full object-cover" alt={bName} />
                ) : (
                  <span className="text-white font-black text-2xl">{bName.charAt(0)}</span>
                )}
              </div>
            </div>

            {/* Right Side: Share + Flag */}
            <div className="flex items-center gap-1">
              {showShare && (
                <button 
                  onClick={() => setShowShareMenu(true)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-full transition-all text-white/60 hover:text-white"
                >
                  <Share2 size={16} strokeWidth={1.5} />
                </button>
              )}
              {showReport && (
                <button 
                  onClick={() => requireAuth(() => setShowReportModal(true), "Log in to report a brand.")}
                  className="w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-full transition-all text-white/60 hover:text-white"
                >
                  <Flag size={16} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </nav>
        </div>

        {/* ✅ Custom Layout Blocks - Using UPDATED renderSectionBody */}
        <div className="flex-1 overflow-y-auto pt-20 pb-24">
          {layoutBlocks.length === 0 ? (
            <div className="flex items-center justify-center h-[60vh] text-zinc-500">
              <div className="text-center">
                <LayoutTemplate size={48} className="mx-auto mb-4 opacity-20" />
                <p className={`text-[12px] font-black uppercase tracking-widest ${themeStyles.text}`}>No layout configured</p>
                <p className={`text-[9px] ${themeStyles.subtext} mt-1`}>The brand hasn't set up their microsite yet.</p>
              </div>
            </div>
          ) : (
            layoutBlocks
              .filter((b: any) => b.visible !== false) // ✅ Respect visibility toggle
              .map((block: any, i: number) => {
                const sectionColor = block.sectionColor || '#000000';
                const opacity = (block.sectionOpacity ?? 100) / 100;
                const padding = block.padding === 'large' ? '3rem' 
                  : block.padding === 'small' ? '0.5rem' 
                  : '1.5rem';

                // ✅ Background styles with opacity (background only)
                const bgStyles: React.CSSProperties = {};
                if (sectionColor?.includes('gradient')) {
                  bgStyles.backgroundImage = sectionColor;
                } else {
                  bgStyles.backgroundColor = sectionColor;
                }
                bgStyles.opacity = opacity;
                bgStyles.position = 'absolute';
                bgStyles.inset = 0;
                bgStyles.zIndex = 0;

                return (
                  <div key={block.id || i} className="relative" style={{ padding, minHeight: '40px' }}>
                    {/* ✅ Background layer with opacity */}
                    <div style={bgStyles} />
                    {/* ✅ Content layer - full opacity */}
                    <div className="relative z-10">
                      {renderSectionBody(block, shopProducts)}
                    </div>
                  </div>
                );
              })
          )}
        </div>

        {/* Footer */}
        <div className="relative shrink-0">
          <div className="absolute bottom-20 left-4 z-10">
            <button 
              onClick={() => requireAuth(() => setShowSupportModal(true), `Log in to contact @${bName}.`)}
              className="w-12 h-12 bg-white text-black rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
            >
              <MessageSquare size={20} fill="currentColor" />
            </button>
          </div>
          
          <div className={`p-4 border-t ${themeStyles.border} bg-black/30 backdrop-blur-sm`}>
            <div className="max-w-6xl mx-auto">
              {footerText && (
                <p className={`text-[10px] ${themeStyles.subtext} text-center`}>{footerText}</p>
              )}
              <div className="flex justify-center gap-4 mt-2">
                {footerSocialLinks.instagram && (
                  <a href={footerSocialLinks.instagram} target="_blank" rel="noreferrer" className={`${themeStyles.subtext} hover:${themeStyles.text} transition-colors`}>
                    <Instagram size={16} />
                  </a>
                )}
                {footerSocialLinks.tiktok && (
                  <a href={footerSocialLinks.tiktok} target="_blank" rel="noreferrer" className={`${themeStyles.subtext} hover:${themeStyles.text} transition-colors`}>
                    <Music2 size={16} />
                  </a>
                )}
                {footerSocialLinks.twitter && (
                  <a href={footerSocialLinks.twitter} target="_blank" rel="noreferrer" className={`${themeStyles.subtext} hover:${themeStyles.text} transition-colors`}>
                    <Twitter size={16} />
                  </a>
                )}
                {footerSocialLinks.facebook && (
                  <a href={footerSocialLinks.facebook} target="_blank" rel="noreferrer" className={`${themeStyles.subtext} hover:${themeStyles.text} transition-colors`}>
                    <Facebook size={16} />
                  </a>
                )}
                {footerSocialLinks.youtube && (
                  <a href={footerSocialLinks.youtube} target="_blank" rel="noreferrer" className={`${themeStyles.subtext} hover:${themeStyles.text} transition-colors`}>
                    <Youtube size={16} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showSupportModal && (
          <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowSupportModal(false)}>
            <div className="bg-zinc-950 border border-white/10 rounded-[2rem] w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
              {supportStatus === 'success' ? (
                <div className="text-center py-8"><CheckCircle className="mx-auto text-green-500 mb-4" size={48}/><h3 className="text-white font-black text-xl uppercase">Message Sent</h3><p className="text-zinc-500 text-sm mt-2">The brand will get back to you shortly.</p></div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-4">
                    <MessageSquare className="text-blue-400" size={24}/>
                    <h3 className="text-white font-black italic uppercase text-xl">Contact Brand</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4">Have an issue with an order from <strong>{bName}</strong> or a general question? Send them a direct message.</p>
                  <textarea 
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder="Type your message here..."
                    className="w-full bg-zinc-900 text-white border border-white/10 rounded-xl p-4 h-32 outline-none focus:border-blue-500 resize-none mb-6 font-mono text-sm"
                  ></textarea>
                  <div className="flex gap-3">
                    <button onClick={() => setShowSupportModal(false)} className="flex-1 py-4 bg-zinc-900 text-white border border-white/10 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-800 transition-colors">Cancel</button>
                    <button onClick={handleSendSupportMessage} disabled={!supportMessage.trim() || supportStatus === 'submitting'} className="flex-1 py-4 bg-blue-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center">
                      {supportStatus === 'submitting' ? <Loader2 size={16} className="animate-spin" /> : 'Send'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showReportModal && (
          <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowReportModal(false)}>
            <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
              {reportStatus === 'success' ? (
                <div className="text-center"><CheckCircle className="mx-auto text-green-500 mb-4" size={40}/><h3 className="text-white font-bold uppercase">Report Sent</h3></div>
              ) : (
                <>
                  <h3 className="text-white font-bold uppercase mb-4 flex items-center gap-2"><AlertTriangle className="text-red-500"/> Report Brand</h3>
                  <p className="text-zinc-500 text-xs mb-4">Reports are sent directly to platform admins.</p>
                  <div className="space-y-2 mb-4">{['Spam', 'Scam', 'Inappropriate', 'Fake Items'].map(r => <button key={r} onClick={() => setReportReason(r)} className={`w-full p-3 text-left border rounded-xl ${reportReason === r ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-white/10 text-zinc-400 hover:bg-white/5'}`}>{r}</button>)}</div>
                  <button onClick={handleSubmitReport} disabled={!reportReason} className="w-full py-4 bg-white text-black rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors">Submit to Admins</button>
                </>
              )}
            </div>
          </div>
        )}

        {showShareMenu && (
          <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowShareMenu(false)}>
            <div className="bg-zinc-900 border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Share2 className="text-white" size={32} />
              </div>
              <h3 className="text-white font-black uppercase italic tracking-widest mb-2">Share Storefront</h3>
              <p className="text-zinc-500 text-xs mb-8 uppercase font-bold tracking-tighter">Grow your audience by sharing your unique link.</p>
              <div className="space-y-3">
                <button onClick={handleNativeShare} className="w-full py-4 bg-white text-black rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center justify-center gap-2">
                  <Users size={16} /> Send to Scoop Friends
                </button>
                <button onClick={handleCopyLink} className="w-full py-4 bg-zinc-800 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-700 transition-all active:scale-95 flex items-center justify-center gap-2">
                  <LinkIcon size={16} /> Copy URL
                </button>
              </div>
              <button onClick={() => setShowShareMenu(false)} className="mt-6 text-zinc-600 uppercase font-black text-[10px] hover:text-white transition-colors tracking-[0.2em]">Dismiss</button>
            </div>
          </div>
        )}

        <FriendShareOverlay 
          isOpen={showFriendOverlay} 
          onClose={() => setShowFriendOverlay(false)} 
          friends={userFriends} 
          brandName={bName}
          onSend={handleSendToFriend}
          loadingFriends={loadingFriends}
        />
      </div>
    );
  }

  // ============================================================================
  // DEFAULT MODE - Traditional brand profile (DARK THEME)
  // ============================================================================
  return (
    <div className={`fixed inset-0 z-[100] flex flex-col animate-in fade-in duration-300 ${themeStyles.bg} ${themeStyles.text} ${bFontClass}`}>
      
      {/* =========================================================================
          📱 MOBILE LAYOUT (< 1024px)
      ========================================================================= */}
      <div className="lg:hidden flex-1 overflow-y-auto relative scrollbar-hide" style={themeStyles.bg === 'bg-white' ? { background: 'white' } : {}}>
          <div className="relative h-64 w-full shrink-0 bg-black">
            {bBanner && <img src={bBanner} className="w-full h-full object-cover opacity-80" alt="" />}
            {/* ✅ Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/90" />
            <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-20">
                <button onClick={onClose} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10">
                  <ArrowLeft size={20} />
                </button>
                <div className="flex gap-2">
                    <button onClick={() => requireAuth(() => setShowSupportModal(true), `Log in to contact @${bName}.`)} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-blue-400 border border-white/10">
                      <MessageSquare size={18} />
                    </button>
                    <button onClick={() => setShowShareMenu(!showShareMenu)} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10">
                      <Share2 size={18} />
                    </button>
                    <button onClick={() => requireAuth(() => setShowReportModal(true), "Log in to report a brand.")} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-red-400 border border-white/10">
                      <Flag size={18} />
                    </button>
                </div>
            </div>
          </div>

          <div className={`px-6 -mt-16 relative z-10 flex flex-col pb-6 ${bAlignClass}`}>
             <div className={`w-24 h-24 rounded-[2rem] border-4 ${themeStyles.border} ${themeStyles.card} overflow-hidden shadow-2xl mb-4 flex items-center justify-center`}>
                {use3DLogo && bLogo ? (
                  <SpinningLogo logoUrl={bLogo} size={96} />
                ) : bLogo ? (
                  <img src={bLogo} className="w-full h-full object-cover" alt="" />
                ) : (
                  <span className={`text-4xl font-black ${themeStyles.subtext} uppercase`}>{bName.charAt(0)}</span>
                )}
             </div>
             
             <div className={`flex items-center gap-2 mb-1 ${themeStyles.text}`}>
                <h1 className="text-3xl font-black uppercase tracking-tight" style={{ WebkitTextStroke: isDarkColor && bColor === '#000000' ? '1px rgba(255,255,255,0.2)' : 'none' }}>{bName}</h1>
                <CheckCircle size={18} style={{ color: isDarkColor && bColor === '#000000' ? '#ffffff' : bColor }} fill="currentColor" />
             </div>
             
             <div className={`flex items-center gap-1 ${themeStyles.subtext} text-[10px] font-black uppercase tracking-widest mb-3`}>
                 <MapPin size={12} /> {bLoc}
             </div>

             <p className={`text-sm ${themeStyles.subtext} font-medium leading-snug mb-4 line-clamp-2`}>{bDesc}</p>
             
             <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 mb-2 w-full max-w-full">
                <button 
                    onClick={handleFollowToggle} 
                    className="shrink-0 px-8 py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-transform active:scale-95"
                    style={{ 
                        backgroundColor: isFollowing ? 'transparent' : bColor, 
                        color: isFollowing ? bColor : getContrastYIQ(bColor), 
                        border: isFollowing ? `1px solid ${bColor}` : (isDarkColor ? `1px solid ${themeStyles.border}` : 'none')
                    }}
                >
                    {isFollowing ? 'Following' : 'Follow'}
                </button>
                {liveBrand.websiteUrl && <a href={liveBrand.websiteUrl} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} ${themeStyles.text} shrink-0`}><Globe size={18}/></a>}
                {liveBrand.instagramHandle && <a href={`https://instagram.com/${liveBrand.instagramHandle}`} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} text-pink-500 shrink-0`}><Instagram size={18}/></a>}
                {liveBrand.twitterHandle && <a href={`https://twitter.com/${liveBrand.twitterHandle}`} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} text-sky-500 shrink-0`}><Twitter size={18}/></a>}
                {liveBrand.facebookHandle && <a href={liveBrand.facebookHandle} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} text-blue-600 shrink-0`}><Facebook size={18}/></a>}
                {liveBrand.youtubeHandle && <a href={liveBrand.youtubeHandle} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} text-red-600 shrink-0`}><Youtube size={18}/></a>}
                {liveBrand.tiktokHandle && <a href={`https://tiktok.com/@${liveBrand.tiktokHandle}`} target="_blank" rel="noreferrer" className={`p-3 ${themeStyles.card} rounded-xl border ${themeStyles.border} text-white shrink-0`}><Music2 size={18}/></a>}
             </div>
          </div>

          <div className="p-4 pb-24">
              {selectedCollection ? (
                  renderCollectionDetails()
              ) : (
                  <>
                      <div className={`flex border-b ${themeStyles.border} sticky top-0 z-20 backdrop-blur-md bg-opacity-80 mb-6`} style={themeStyles.bg === 'bg-white' ? { background: 'white' } : { background: 'black' }}>
                          {['shop', 'collections', 'about'].map(t => (
                              <button 
                                  key={t} 
                                  onClick={() => setActiveTab(t as any)} 
                                  className="flex-1 py-4 text-[10px] font-black uppercase tracking-[0.2em] transition-colors"
                                  style={{ color: activeTab === t ? (isDarkColor && bColor === '#000000' ? '#ffffff' : bColor) : '#71717a', borderBottom: activeTab === t ? `2px solid ${isDarkColor && bColor === '#000000' ? '#ffffff' : bColor}` : 'none' }}
                              >
                                  {t}
                              </button>
                          ))}
                      </div>

                      {activeTab === 'shop' && (
                          <div className={`space-y-6 ${themeStyles.text}`}>
                              {giveaways.length > 0 && <div className="space-y-3"><h3 className={`text-[10px] font-bold uppercase ${themeStyles.subtext}`}>Giveaways</h3>{giveaways.map(d => <ProfileGiveawayCard key={d.id} drop={d} profile={userProfile} onOpenProduct={() => onSelectProduct(d)} themeStyles={themeStyles} />)}</div>}
                              {shopProducts.length === 0 ? (
                                <div className={`text-center py-20 border ${themeStyles.border} border-dashed rounded-3xl ${themeStyles.card}`}>
                                  <Package className={`mx-auto ${themeStyles.subtext} mb-4`} size={32} />
                                  <h3 className={`${themeStyles.text} font-black uppercase tracking-widest text-sm mb-2`}>Drop Coming Soon</h3>
                                  <p className={`${themeStyles.subtext} text-xs font-bold uppercase tracking-widest`}>This brand is preparing their collection.</p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-2 gap-4">
                                  {shopProducts.map((p: any) => <ProductCard key={p.id} product={p} onClick={() => {onClose(); onSelectProduct(p)}} isWishlisted={isWishlisted(p.id)} onToggleWishlist={() => onToggleWishlist && onToggleWishlist(p.id)} themeStyles={themeStyles} />)}
                                </div>
                              )}
                          </div>
                      )}
                      
                      {activeTab === 'collections' && (
                          <div className="grid grid-cols-1 gap-4">
                              {brandCollections.length === 0 && <div className={`text-center ${themeStyles.subtext} text-xs py-10 uppercase tracking-widest font-bold`}>No Collections Yet</div>}
                              {brandCollections.map(col => (
                                  <div 
                                      key={col.id} 
                                      onClick={() => setSelectedCollection(col)} 
                                      className={`aspect-[2/1] ${themeStyles.card} border ${themeStyles.border} relative group cursor-pointer overflow-hidden rounded-2xl shadow-xl`}
                                  >
                                      <img src={col.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt="" />
                                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-4 text-center">
                                          <h3 className={`text-2xl font-black italic uppercase ${themeStyles.text} tracking-widest`}>{col.title}</h3>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}

                      {activeTab === 'about' && (
                          <div className={`space-y-4 ${themeStyles.text}`}>
                              <div className={`${themeStyles.card} rounded-2xl p-6 border ${themeStyles.border}`}>
                                  <h3 className={`text-xs font-black uppercase tracking-widest ${themeStyles.text} mb-3 flex items-center gap-2`}><Info size={14}/> About</h3>
                                  <p className={`text-sm ${themeStyles.subtext} leading-relaxed`}>{bDesc}</p>
                              </div>
                              <div className={`${themeStyles.card} rounded-2xl p-6 border ${themeStyles.border}`}>
                                  <h3 className={`text-xs font-black uppercase tracking-widest ${themeStyles.text} mb-3 flex items-center gap-2`}><Store size={14}/> Location</h3>
                                  <div className={`flex items-center gap-3 ${themeStyles.subtext} text-sm`}><MapPin size={18} /><span>{bLoc}</span></div>
                              </div>
                              <div className={`${themeStyles.card} rounded-2xl p-6 border ${themeStyles.border}`}>
                                  <h3 className={`text-xs font-black uppercase tracking-widest ${themeStyles.text} mb-3 flex items-center gap-2`}><ShieldCheck size={14}/> Returns</h3>
                                  <p className={`text-sm ${themeStyles.subtext} leading-relaxed`}>{liveBrand.returnPolicy || "Standard return policy."}</p>
                              </div>
                              {(liveBrand.emailSupport || liveBrand.email) && (
                                  <div className={`${themeStyles.card} rounded-2xl p-6 border ${themeStyles.border}`}>
                                      <h3 className={`text-xs font-black uppercase tracking-widest ${themeStyles.text} mb-3 flex items-center gap-2`}><Mail size={14}/> Contact</h3>
                                      <a href={`mailto:${liveBrand.emailSupport || liveBrand.email}`} className={`flex items-center gap-3 ${themeStyles.text} text-sm font-bold`}><Mail size={18}/> {liveBrand.emailSupport || liveBrand.email}</a>
                                  </div>
                              )}
                          </div>
                      )}
                  </>
              )}
          </div>
      </div>

      {/* =========================================================================
          🖥️ DESKTOP LAYOUT (>= 1024px)
      ========================================================================= */}
      <div className="hidden lg:flex w-full h-full" style={themeStyles.bg === 'bg-white' ? { background: 'white' } : {}}>
          <div className={`w-[400px] xl:w-[500px] border-r ${themeStyles.border} flex flex-col h-full overflow-y-auto shrink-0`} style={themeStyles.bg === 'bg-white' ? { background: 'white' } : { background: 'black' }}>
              <div className="h-64 w-full relative group shrink-0 bg-black">
                  {bBanner && <img src={bBanner} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt="" />}
                  {/* ✅ Dark gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/90" />
                  <div className="absolute top-6 left-6 z-10">
                    <button onClick={onClose} className="p-2 bg-black/60 backdrop-blur-md text-white hover:bg-white hover:text-black transition-colors border border-white/20 rounded-xl">
                      <ArrowLeft size={20} />
                    </button>
                  </div>
              </div>

              <div className={`p-12 flex flex-col flex-1 ${bAlignClass}`}>
                  <div className={`w-24 h-24 border ${themeStyles.border} ${themeStyles.card} mb-8 overflow-hidden rounded-2xl shrink-0 flex items-center justify-center`}>
                      {use3DLogo && bLogo ? (
                        <SpinningLogo logoUrl={bLogo} size={96} />
                      ) : bLogo ? (
                        <img src={bLogo} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <span className={`text-5xl font-black ${themeStyles.subtext} uppercase`}>{bName.charAt(0)}</span>
                      )}
                  </div>
                  
                  <h1 className={`text-5xl xl:text-6xl font-black italic uppercase tracking-tighter leading-[0.8] mb-4 ${themeStyles.text}`} style={{ WebkitTextStroke: isDarkColor && bColor === '#000000' ? '1px rgba(255,255,255,0.2)' : 'none' }}>{bName}</h1>
                  <p className={`text-sm ${themeStyles.subtext} font-bold uppercase tracking-widest mb-8 line-clamp-3`}>{bDesc}</p>

                  <div className={`grid grid-cols-2 gap-4 w-full mb-8 text-xs font-mono ${themeStyles.subtext} border-y ${themeStyles.border} py-6 text-left`}>
                      <div><span className={`block ${themeStyles.text} font-bold`}>LOC</span> {bLoc}</div>
                      <div><span className={`block ${themeStyles.text} font-bold`}>FLLW</span> {displayFollowers}</div>
                      {liveBrand.websiteUrl && <div><span className={`block ${themeStyles.text} font-bold`}>WEB</span> <a href={liveBrand.websiteUrl} target="_blank" rel="noreferrer" className={`hover:${themeStyles.text} underline`} style={{ color: isDarkColor && bColor === '#000000' ? '#ffffff' : bColor }}>Link</a></div>}
                  </div>

                  <div className={`flex gap-2 mb-8 flex-wrap ${bAlignClass === 'items-center text-center' ? 'justify-center' : bAlignClass === 'items-end text-right' ? 'justify-end' : 'justify-start'} w-full`}>
                      {liveBrand.instagramHandle && <a href={`https://instagram.com/${liveBrand.instagramHandle}`} target="_blank" rel="noreferrer" className={`p-2 border ${themeStyles.border} hover:bg-white hover:text-black transition-colors rounded-lg ${themeStyles.text}`}><Instagram size={16}/></a>}
                      {liveBrand.twitterHandle && <a href={`https://twitter.com/${liveBrand.twitterHandle}`} target="_blank" rel="noreferrer" className={`p-2 border ${themeStyles.border} hover:bg-white hover:text-black transition-colors rounded-lg ${themeStyles.text}`}><Twitter size={16}/></a>}
                      {liveBrand.facebookHandle && <a href={liveBrand.facebookHandle} target="_blank" rel="noreferrer" className={`p-2 border ${themeStyles.border} hover:bg-white hover:text-black transition-colors rounded-lg ${themeStyles.text}`}><Facebook size={16}/></a>}
                      {liveBrand.tiktokHandle && <a href={`https://tiktok.com/@${liveBrand.tiktokHandle}`} target="_blank" rel="noreferrer" className={`p-2 border ${themeStyles.border} hover:bg-white hover:text-black transition-colors rounded-lg ${themeStyles.text}`}><Music2 size={16}/></a>}
                  </div>

                  <div className="mt-auto space-y-3 w-full">
                      <button 
                          onClick={handleFollowToggle} 
                          className="w-full py-4 font-black text-xs uppercase tracking-[0.2em] transition-colors rounded-xl"
                          style={{ 
                              backgroundColor: isFollowing ? 'transparent' : bColor, 
                              color: isFollowing ? bColor : getContrastYIQ(bColor), 
                              border: isFollowing ? `1px solid ${bColor}` : (isDarkColor ? `1px solid ${themeStyles.border}` : `1px solid ${bColor}`)
                          }}
                      >
                          {isFollowing ? 'Following' : 'Follow Brand'}
                      </button>
                      <div className="flex gap-3">
                          <button onClick={() => requireAuth(() => setShowSupportModal(true), `Log in to contact @${bName}.`)} className={`flex-1 py-4 border ${themeStyles.border} rounded-xl ${themeStyles.subtext} hover:text-blue-400 hover:border-blue-400 uppercase text-[10px] font-bold tracking-widest transition-colors flex justify-center items-center gap-2`}>
                            <MessageSquare size={14}/> Contact
                          </button>
                          <button onClick={() => setShowShareMenu(!showShareMenu)} className={`flex-1 py-4 border ${themeStyles.border} rounded-xl ${themeStyles.subtext} hover:text-white hover:border-white uppercase text-[10px] font-bold tracking-widest transition-colors`}>
                            Share
                          </button>
                          <button onClick={() => requireAuth(() => setShowReportModal(true), "Log in to report a brand.")} className={`flex-1 py-4 border ${themeStyles.border} rounded-xl ${themeStyles.subtext} hover:text-red-500 hover:border-red-500 uppercase text-[10px] font-bold tracking-widest transition-colors`}>
                            <Flag size={14}/> Report
                          </button>
                      </div>
                  </div>
              </div>
          </div>

          <div className="flex-1 flex flex-col h-full" style={themeStyles.bg === 'bg-white' ? { background: 'white' } : { background: 'black' }}>
              {selectedCollection ? (
                  <div className="flex-1 overflow-y-auto p-12">
                      {renderCollectionDetails()}
                  </div>
              ) : (
                  <>
                      <div className={`flex border-b ${themeStyles.border} sticky top-0 z-10`} style={themeStyles.bg === 'bg-white' ? { background: 'white' } : { background: 'black' }}>
                          {['shop', 'collections', 'about'].map(t => (
                              <button 
                                  key={t} 
                                  onClick={() => setActiveTab(t as any)} 
                                  className="px-12 py-6 text-xs font-bold uppercase tracking-[0.2em] border-r transition-colors"
                                  style={{ color: activeTab === t ? (isDarkColor && bColor === '#000000' ? '#ffffff' : bColor) : '#71717a', borderColor: themeStyles.border, borderBottom: activeTab === t ? `2px solid ${isDarkColor && bColor === '#000000' ? '#ffffff' : bColor}` : 'none' }}
                              >
                                  {t}
                              </button>
                          ))}
                      </div>

                      <div className="flex-1 overflow-y-auto p-12">
                          {activeTab === 'shop' && (
                              <div className={`space-y-16 ${themeStyles.text}`}>
                                  {giveaways.length > 0 && (
                                      <section>
                                          <div className={`flex items-center gap-4 mb-8 border-b ${themeStyles.border} pb-4`}>
                                              <Ticket size={20} style={{ color: isDarkColor && bColor === '#000000' ? '#ffffff' : bColor }} />
                                              <h3 className={`text-2xl font-black italic uppercase ${themeStyles.text}`}>Active Raffles</h3>
                                          </div>
                                          <div className="grid grid-cols-2 gap-8">
                                              {giveaways.map(d => (
                                                  <ProfileGiveawayCard key={d.id} drop={d} profile={userProfile} onOpenProduct={() => onSelectProduct(d)} themeStyles={themeStyles} />
                                              ))}
                                          </div>
                                      </section>
                                  )}

                                  <section>
                                      <div className={`flex items-center gap-4 mb-8 border-b ${themeStyles.border} pb-4`}>
                                          <LayoutGrid size={20} style={{ color: isDarkColor && bColor === '#000000' ? '#ffffff' : bColor }} />
                                          <h3 className={`text-2xl font-black italic uppercase ${themeStyles.text}`}>Collection</h3>
                                      </div>
                                      {shopProducts.length === 0 ? (
                                        <div className={`text-center py-20 border ${themeStyles.border} border-dashed rounded-3xl ${themeStyles.card}`}>
                                          <Package className={`mx-auto ${themeStyles.subtext} mb-4`} size={48} />
                                          <h3 className={`${themeStyles.text} font-black uppercase tracking-widest text-lg mb-2`}>Drop Coming Soon</h3>
                                          <p className={`${themeStyles.subtext} text-sm font-bold uppercase tracking-widest`}>This brand is preparing their collection.</p>
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-4 gap-x-6 gap-y-12">
                                          {shopProducts.map((p: any) => (
                                              <ProductCard key={p.id} product={p} onClick={() => { onClose(); onSelectProduct(p); }} isWishlisted={isWishlisted(p.id)} onToggleWishlist={() => onToggleWishlist && onToggleWishlist(p.id)} themeStyles={themeStyles} />
                                          ))}
                                        </div>
                                      )}
                                  </section>
                              </div>
                          )}

                          {activeTab === 'collections' && (
                              <div className="grid grid-cols-2 gap-8">
                                  {brandCollections.length === 0 && <div className={`col-span-2 ${themeStyles.subtext} font-bold uppercase tracking-widest`}>No Collections Found</div>}
                                  {brandCollections.map(col => (
                                      <div 
                                          key={col.id} 
                                          onClick={() => setSelectedCollection(col)}
                                          className={`aspect-[4/3] ${themeStyles.card} border ${themeStyles.border} relative group cursor-pointer overflow-hidden rounded-[2rem] shadow-2xl`}
                                      >
                                          <img src={col.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt="" />
                                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                              <h3 className={`text-3xl font-black italic uppercase ${themeStyles.text} tracking-widest`} style={{ color: isDarkColor && bColor === '#000000' ? '#ffffff' : bColor }}>{col.title}</h3>
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          )}

                          {activeTab === 'about' && (
                              <div className={`max-w-2xl ${themeStyles.subtext} leading-relaxed font-mono text-sm`}>
                                  <p className={`mb-8 ${themeStyles.text}`}>{bDesc}</p>
                                  <div className="grid grid-cols-2 gap-8">
                                      <div className={`p-8 border ${themeStyles.border} rounded-3xl ${themeStyles.card}`}>
                                          <h4 className={`${themeStyles.text} font-bold uppercase mb-4 flex items-center gap-2`}><Mail size={16}/> Contact</h4>
                                          <p className="mb-1">Email: {liveBrand.emailSupport || liveBrand.email || 'N/A'}</p>
                                      </div>
                                      <div className={`p-8 border ${themeStyles.border} rounded-3xl ${themeStyles.card}`}>
                                          <h4 className={`${themeStyles.text} font-bold uppercase mb-4 flex items-center gap-2`}><Store size={16}/> Address</h4>
                                          <p>{bLoc}</p>
                                      </div>
                                      <div className={`p-8 border ${themeStyles.border} col-span-2 rounded-3xl ${themeStyles.card}`}>
                                          <h4 className={`${themeStyles.text} font-bold uppercase mb-4 flex items-center gap-2`}><ShieldCheck size={16}/> Policy</h4>
                                          <p>{liveBrand.returnPolicy || "Standard return policy applies."}</p>
                                      </div>
                                  </div>
                              </div>
                          )}
                      </div>
                  </>
              )}
          </div>
      </div>

      {/* --- MODALS --- */}
      {showSupportModal && (
          <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowSupportModal(false)}>
              <div className="bg-zinc-950 border border-white/10 rounded-[2rem] w-full max-w-md p-8 shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
                  {supportStatus === 'success' ? (
                      <div className="text-center py-8"><CheckCircle className="mx-auto text-green-500 mb-4" size={48}/><h3 className="text-white font-black text-xl uppercase">Message Sent</h3><p className="text-zinc-500 text-sm mt-2">The brand will get back to you shortly.</p></div>
                  ) : (
                      <>
                        <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-4">
                            <MessageSquare className="text-blue-400" size={24}/>
                            <h3 className="text-white font-black italic uppercase text-xl">Contact Brand</h3>
                        </div>
                        <p className="text-zinc-400 text-sm mb-4">Have an issue with an order from <strong>{bName}</strong> or a general question? Send them a direct message.</p>
                        <textarea 
                            value={supportMessage}
                            onChange={(e) => setSupportMessage(e.target.value)}
                            placeholder="Type your message here..."
                            className="w-full bg-zinc-900 text-white border border-white/10 rounded-xl p-4 h-32 outline-none focus:border-blue-500 resize-none mb-6 font-mono text-sm"
                        ></textarea>
                        <div className="flex gap-3">
                            <button onClick={() => setShowSupportModal(false)} className="flex-1 py-4 bg-zinc-900 text-white border border-white/10 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-800 transition-colors">Cancel</button>
                            <button onClick={handleSendSupportMessage} disabled={!supportMessage.trim() || supportStatus === 'submitting'} className="flex-1 py-4 bg-blue-500 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center">
                                {supportStatus === 'submitting' ? <Loader2 size={16} className="animate-spin" /> : 'Send'}
                            </button>
                        </div>
                      </>
                  )}
              </div>
          </div>
      )}

      {showReportModal && (
          <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowReportModal(false)}>
              <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
                  {reportStatus === 'success' ? (
                      <div className="text-center"><CheckCircle className="mx-auto text-green-500 mb-4" size={40}/><h3 className="text-white font-bold uppercase">Report Sent</h3></div>
                  ) : (
                      <>
                        <h3 className="text-white font-bold uppercase mb-4 flex items-center gap-2"><AlertTriangle className="text-red-500"/> Report Brand</h3>
                        <p className="text-zinc-500 text-xs mb-4">Reports are sent directly to platform admins.</p>
                        <div className="space-y-2 mb-4">{['Spam', 'Scam', 'Inappropriate', 'Fake Items'].map(r => <button key={r} onClick={() => setReportReason(r)} className={`w-full p-3 text-left border rounded-xl ${reportReason === r ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-white/10 text-zinc-400 hover:bg-white/5'}`}>{r}</button>)}</div>
                        <button onClick={handleSubmitReport} disabled={!reportReason} className="w-full py-4 bg-white text-black rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors">Submit to Admins</button>
                      </>
                  )}
              </div>
          </div>
      )}

      {showShareMenu && (
          <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-6" onClick={() => setShowShareMenu(false)}>
              <div className="bg-zinc-900 border border-white/10 rounded-[2rem] p-8 w-full max-w-sm text-center shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Share2 className="text-white" size={32} />
                  </div>
                  <h3 className="text-white font-black uppercase italic tracking-widest mb-2">Share Storefront</h3>
                  <p className="text-zinc-500 text-xs mb-8 uppercase font-bold tracking-tighter">Grow your audience by sharing your unique link.</p>
                  <div className="space-y-3">
                    <button onClick={handleNativeShare} className="w-full py-4 bg-white text-black rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center justify-center gap-2">
                      <Users size={16} /> Send to Scoop Friends
                    </button>
                    <button onClick={handleCopyLink} className="w-full py-4 bg-zinc-800 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-700 transition-all active:scale-95 flex items-center justify-center gap-2">
                      <LinkIcon size={16} /> Copy URL
                    </button>
                  </div>
                  <button onClick={() => setShowShareMenu(false)} className="mt-6 text-zinc-600 uppercase font-black text-[10px] hover:text-white transition-colors tracking-[0.2em]">Dismiss</button>
              </div>
          </div>
      )}

      <FriendShareOverlay 
        isOpen={showFriendOverlay} 
        onClose={() => setShowFriendOverlay(false)} 
        friends={userFriends} 
        brandName={bName}
        onSend={handleSendToFriend}
        loadingFriends={loadingFriends}
      />
    </div>
  );
};

// ============================================================================
// TYPES
// ============================================================================
interface BrandProfileViewProps {
  brand: BrandProfile;
  products: Product[];
  onClose: () => void;
  onSelectProduct: (product: Product) => void;
  userProfile?: UserProfile;
  onUpdateProfile?: (data: Partial<UserProfile>) => void;
  wishlist?: any[];
  onToggleWishlist?: (productId: string) => void;
  onShareProfile?: () => void;
  requireAuth: (action: () => void, message: string) => void;
}

export default BrandProfileView;